/** The replay stream is a bounded DOM tree, never a network, audio or desktop recording. */
export const REPLAY_ROUTES = ['home', 'capture', 'moment', 'landing', 'exercise', 'round'] as const;
export type ReplayRoute = typeof REPLAY_ROUTES[number];

export interface ReplayTextNode { kind: 'text'; text: string }
export interface ReplayElementNode {
  kind: 'element';
  tag: string;
  attrs: Record<string, string>;
  children: ReplayNode[];
  shadow?: ReplayNode[];
}
export type ReplayNode = ReplayTextNode | ReplayElementNode;

export interface ReplayFrameInput {
  sessionId: string;
  attemptId: string;
  /** Server-issued generation; frames from a stopped/reset/previous consent are refused. */
  generation: string;
  sequence: number;
  capturedAt: string;
  route: ReplayRoute;
  viewport: { width: number; height: number };
  tree: ReplayNode;
}

export interface ReplayFrame extends ReplayFrameInput { receivedAt: string }

export const REPLAY_FRAME_MAX_BYTES = 512 * 1024;
export const REPLAY_FRAME_MAX_NODES = 6_000;
export const REPLAY_FRAME_MAX_DEPTH = 48;
export const REPLAY_FRAME_MAX_TEXT = 120_000;
export const REPLAY_FRAMES_PER_ATTEMPT_MAX = 600;
export const REPLAY_FRAMES_PER_PARTICIPANT_MAX = 1_200;
export const REPLAY_RETENTION_DAYS_DEFAULT = 30;
export const REPLAY_RETENTION_DAYS_MAX = 90;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TAGS = new Set([
  'a', 'abbr', 'article', 'aside', 'b', 'bdi', 'blockquote', 'br', 'button', 'caption', 'circle', 'code', 'dd', 'defs', 'details',
  'body', 'div', 'dl', 'dt', 'em', 'fieldset', 'figcaption', 'figure', 'footer', 'g', 'h1', 'h2', 'h3', 'h4', 'header', 'hr',
  'i', 'label', 'legend', 'li', 'lineargradient', 'main', 'mark', 'nav', 'ol', 'option', 'p', 'path', 'polygon', 'polyline',
  'rect', 'section', 'small', 'span', 'stop', 'strong', 'style', 'sub', 'summary', 'sup', 'svg', 'table', 'tbody', 'td',
  'th', 'thead', 'time', 'tr', 'u', 'ul',
]);
const ATTRS = new Set([
  'aria-label', 'aria-labelledby', 'aria-modal', 'aria-pressed', 'aria-current', 'aria-expanded', 'aria-selected', 'class', 'checked', 'cx', 'cy', 'd', 'dir',
  'disabled', 'fill', 'fill-rule', 'height', 'id', 'lang', 'points', 'r', 'role', 'selected', 'stroke', 'stroke-linecap',
  'stroke-linejoin', 'stroke-width', 'tabindex', 'transform', 'viewbox', 'width', 'x', 'y',
]);
const PRIVATE_CLASS = /(?:^|[-_ ])(?:password|credential|secret|token|invite|invitation|recovery|auth)(?:$|[-_ ])/i;
const OPAQUE_TOKEN = /\b[A-Za-z0-9_-]{20,}\b/g;
const TOKEN_PATH = /\/(?:join|recover|friend|circle\/join)\/[^\s/?#]+/gi;
const TOKEN_QUERY = /([?&#](?:token|secret|code|invite|invitation|recovery)=)[^&#\s]+/gi;
const FORBIDDEN_CSS = /(?:url\s*\(|@import|expression\s*\(|javascript:|behavior\s*:)/i;

function redactVisibleText(value: string): string {
  return value.replace(TOKEN_PATH, (match) => `${match.slice(0, match.lastIndexOf('/') + 1)}[redacted]`)
    .replace(TOKEN_QUERY, '$1[redacted]')
    .replace(OPAQUE_TOKEN, '[redacted]');
}

/** Validate and copy an untrusted browser snapshot into the much smaller server contract. */
export function parseReplayFrame(value: unknown, receivedAt: string): ReplayFrame | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (Object.keys(raw).some((key) => !['sessionId', 'attemptId', 'generation', 'sequence', 'capturedAt', 'route', 'viewport', 'tree'].includes(key))) return null;
  if (typeof raw.sessionId !== 'string' || !UUID.test(raw.sessionId) || typeof raw.attemptId !== 'string' || !UUID.test(raw.attemptId)) return null;
  if (typeof raw.generation !== 'string' || !UUID.test(raw.generation)) return null;
  if (!Number.isInteger(raw.sequence) || Number(raw.sequence) < 0 || Number(raw.sequence) >= 10_000) return null;
  if (typeof raw.capturedAt !== 'string' || raw.capturedAt.length > 40 || !Number.isFinite(Date.parse(raw.capturedAt))) return null;
  if (typeof raw.route !== 'string' || !(REPLAY_ROUTES as readonly string[]).includes(raw.route)) return null;
  if (!raw.viewport || typeof raw.viewport !== 'object' || Array.isArray(raw.viewport)) return null;
  const viewport = raw.viewport as Record<string, unknown>;
  if (Object.keys(viewport).some((key) => key !== 'width' && key !== 'height') ||
      !Number.isInteger(viewport.width) || Number(viewport.width) < 1 || Number(viewport.width) > 4_000 ||
      !Number.isInteger(viewport.height) || Number(viewport.height) < 1 || Number(viewport.height) > 4_000) return null;

  let nodes = 0;
  let textLength = 0;
  const copyNode = (node: unknown, depth: number): ReplayNode | null => {
    if (++nodes > REPLAY_FRAME_MAX_NODES || depth > REPLAY_FRAME_MAX_DEPTH || !node || typeof node !== 'object' || Array.isArray(node)) return null;
    const n = node as Record<string, unknown>;
    if (n.kind === 'text') {
      if (Object.keys(n).some((key) => key !== 'kind' && key !== 'text') || typeof n.text !== 'string' || n.text.length > REPLAY_FRAME_MAX_TEXT) return null;
      textLength += n.text.length;
      if (textLength > REPLAY_FRAME_MAX_TEXT) return null;
      return { kind: 'text', text: redactVisibleText(n.text) };
    }
    const tag = typeof n.tag === 'string' ? n.tag.toLowerCase() : '';
    const safeCustomElement = /^[a-z][a-z0-9]*-[a-z0-9-]{0,80}$/.test(tag);
    if (n.kind !== 'element' || Object.keys(n).some((key) => !['kind', 'tag', 'attrs', 'children', 'shadow'].includes(key)) ||
        !tag || (!TAGS.has(tag) && !safeCustomElement) || !n.attrs || typeof n.attrs !== 'object' || Array.isArray(n.attrs) ||
        !Array.isArray(n.children) || (n.shadow !== undefined && !Array.isArray(n.shadow))) return null;
    const attrs: Record<string, string> = {};
    const sourceAttrs = n.attrs as Record<string, unknown>;
    for (const [key, item] of Object.entries(sourceAttrs)) {
      if (!ATTRS.has(key.toLowerCase()) || typeof item !== 'string' || item.length > 2_000) return null;
      if (key.toLowerCase() === 'class' && PRIVATE_CLASS.test(item)) return null;
      if (tag === 'style') {
        if (key !== 'class') return null;
      } else {
        if (['fill', 'stroke'].includes(key.toLowerCase()) && /url\s*\(/i.test(item)) continue;
        attrs[key] = redactVisibleText(item);
      }
    }
    const children: ReplayNode[] = [];
    for (const child of n.children) {
      const copied = copyNode(child, depth + 1);
      if (!copied) return null;
      children.push(copied);
    }
    let shadow: ReplayNode[] | undefined;
    if (Array.isArray(n.shadow)) {
      shadow = [];
      for (const child of n.shadow) {
        const copied = copyNode(child, depth + 1);
        if (!copied) return null;
        shadow.push(copied);
      }
    }
    if (tag === 'style') {
      const css = children.map((child) => child.kind === 'text' ? child.text : '').join('');
      if (css.length > 32_000 || FORBIDDEN_CSS.test(css)) return null;
    }
    const result: ReplayElementNode = { kind: 'element', tag, attrs, children };
    if (shadow) result.shadow = shadow;
    return result;
  };

  const tree = copyNode(raw.tree, 0);
  if (!tree) return null;
  const frame = {
    sessionId: raw.sessionId.toLowerCase(), attemptId: raw.attemptId.toLowerCase(), generation: raw.generation.toLowerCase(), sequence: Number(raw.sequence),
    capturedAt: new Date(raw.capturedAt).toISOString(), route: raw.route as ReplayRoute,
    viewport: { width: Number(viewport.width), height: Number(viewport.height) }, tree, receivedAt,
  } satisfies ReplayFrame;
  if (Buffer.byteLength(JSON.stringify(frame), 'utf8') > REPLAY_FRAME_MAX_BYTES) return null;
  return frame;
}
